import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";

/** English fallback values – mirrors the edge function master dictionary */
const EN_DEFAULTS: Record<string, string> = {
  "nav.experiences": "Experiences",
  "nav.destinations": "Destinations",
  "nav.partner": "Partner",
  "nav.affiliateProgram": "Affiliate Program",
  "nav.about": "About",
  "nav.menu": "Menu",
  "hero.headline": "Experiences you love.",
  "hero.subline": "Thoughtfully chosen moments, guided by people who know this place.",
  "hero.cta": "Explore experiences",
  "brandValues.0.heading": "Moments that hold you, not rush you",
  "brandValues.0.body": "Each experience is shaped around presence. No checklists, no hurrying through.",
  "brandValues.1.heading": "Led by hosts who understand stillness",
  "brandValues.1.body": "Local guides who know this place deeply, and share it with care.",
  "brandValues.2.heading": "Free cancellation, always",
  "brandValues.2.body": "Plans change. We understand. Cancel for free, no questions asked.",
  "featured.heading": "Worth your time",
  "allExperiences.heading": "All experiences",
  "testimonials.eyebrow": "Guest voices",
  "testimonials.heading": "Moments worth remembering",
  "testimonials.trustBadge": "Every experience is reviewed and quality-checked before listing",
  "card.freeCancellation": "Free cancellation",
  "card.pricingOnEnquiry": "Pricing on enquiry",
  "detail.aboutHeading": "About this experience",
  "detail.whatYoullDo": "What you'll do",
  "detail.whatsIncluded": "What's included",
  "detail.freeCancellation": "Free cancellation",
  "detail.bookNowPayLater": "Book now, pay later",
  "detail.bookNowPayLaterDesc": "Stay flexible — book now, pay in connection with your experience.",
  "detail.goodToKnow": "Good to know",
  "detail.guestRequirements": "Guest requirements",
  "detail.accessibility": "Accessibility",
  "detail.accessibilityText": "Contact your host for more information about accessibility and special needs.",
  "detail.hostedBy": "Hosted by",
  "detail.reserveYourSpot": "Reserve your spot",
  "detail.chooseDate": "Choose a date and time above to get started.",
  "detail.numberOfGuests": "Number of guests",
  "detail.yourName": "Your name",
  "detail.email": "Email",
  "detail.phone": "Phone (optional)",
  "detail.notes": "Notes (optional)",
  "detail.continueToPayment": "Continue to payment",
  "detail.securityNote": "To keep your booking secure, always communicate and pay through Guestly.",
  "filter.all": "All",
  "noExperiences": "No experiences found",
  "clearFilter": "Clear filter",
  "reviews.heading": "Guest Reviews",
  "reviews.verified": "Verified booking",
  "reviews.photosFromGuests": "Photos from guests",
  "destination.heroSubline": "Curated experiences, guided by people who know this place.",
  "destination.cta": "Explore experiences in {city}",
  "destination.seasonSummer": "Summer",
  "destination.seasonWinter": "Winter",
  "destination.seasonYearRound": "Year-round",
  "destination.insiderTips": "Local insider tips",
  "destination.foodCulture": "Local food and culture",
  "destination.wellness": "Relaxation and wellness",
  "destination.bookCta": "We're here if you need anything",
  "filter.season": "Season",
  "season.year_round": "Year-round",
  "season.summer": "Summer",
  "season.winter": "Winter",
  "season.spring": "Spring",
  "season.autumn": "Autumn",
  "season.backIn": "Back in {season}",
  "tldr.heading": "Quick Guide",
  "tldr.bestFor": "Best for",
  "tldr.topExperiences": "Top experiences",
  "tldr.bestSeason": "Best season",
  "tldr.idealStay": "Ideal stay",
  "tldr.whyGuestly": "Why stay with Guestly",
  // QualityBadge
  "quality.heading.nature": "Nature experiences are quality-reviewed",
  "quality.desc.nature": "Led by guides who know this landscape. Each one is checked before it goes live.",
  "quality.heading.adventure": "Adventure experiences are quality-reviewed",
  "quality.desc.adventure": "Led by hosts with local expertise and safety know-how. Each one is checked before it goes live.",
  "quality.heading.foodDrink": "Food experiences are quality-reviewed",
  "quality.desc.foodDrink": "Led by hosts who know this region's flavours. Each one is checked before it goes live.",
  "quality.heading.culture": "Cultural experiences are quality-reviewed",
  "quality.desc.culture": "Led by hosts with deep local knowledge. Each one is checked before it goes live.",
  "quality.heading.wellness": "Wellness experiences are quality-reviewed",
  "quality.desc.wellness": "Led by hosts focused on presence and care. Each one is checked before it goes live.",
  "quality.heading.fallback": "This experience is quality-reviewed",
  "quality.desc.fallback": "Led by a local host. Each experience is checked before it goes live.",
  "quality.learnMore": "Learn more",
  "quality.seeProblem": "See a problem?",
  "quality.reportListing": "Report this listing",
  "quality.modalTitle": "How we review experiences",
  "quality.modalDesc": "Every experience goes through a multi-step review before it's listed.",
  "quality.step.company": "Company details and registration number verified.",
  "quality.step.insurance": "Insurance and required documents reviewed.",
  "quality.step.safety": "Activity safety specifications assessed.",
  "quality.step.portfolio": "Portfolio reviewed — minimum 10 photos required.",
  "quality.step.certifications": "Relevant certifications checked.",
  "quality.step.legal": "Legal agreement signed by the host.",
  "quality.step.quiz": "Safety and quality quiz passed (80% minimum).",
  "quality.step.editorial": "Final editorial review before going live.",
  // New detail sections
  "detail.notSuitableFor": "Not suitable for",
  "detail.importantInfo": "Important information",
  "detail.whatToBring": "What to bring",
  "detail.showMore": "Show more",
  "detail.meetingPoint": "Meeting point",
  "detail.openInMaps": "Open in Google Maps →",
  "detail.relatedExperiences": "Other alternatives that can suit you",
  // GoodToKnow
  "detail.beforeYouGo": "Before you go",
  "detail.activityLevel": "Activity level",
  "detail.cancellation": "Cancellation",
  "detail.readMore": "Read more",
  "activity.easy": "Easy – For everyone (families, elderly, beginners)",
  "activity.moderate": "Moderate – Requires some physical effort or outdoor experience",
  "activity.hard": "Hard – For experienced outdoor enthusiasts or adventurers",
  "activity.expert": "Expert – Requires technical knowledge, certification, or experience",
  "detail.difficultyLevel": "Difficulty level",
  // FloatingBookingPanel
  "booking.showAllDates": "Show all {count} dates",
  "booking.spotsLeft": "{count} spots left",
  "booking.noSlots": "No times available right now. New slots open regularly — check back soon.",
  "booking.onlyDate": "This is the only available date.",
  "booking.datesAvailable": "{count} dates available.",
  "booking.changeDate": "Change date",
  "booking.from": "From",
  "booking.perGuest": "/guest",
  "booking.perGroup": "/group",
  "booking.freeCancellation": "Free cancellation",
  "booking.usuallyBookedOut": "Usually booked out",
  "booking.addedToCart": "Added to cart!",
  "booking.addToCart": "Add to cart",
  "booking.allAvailableDates": "All available dates",
  "booking.spots": "spots",
  // Guest form
  "form.adults": "Adults",
  "form.children": "Children",
  "form.toddlers": "Toddlers",
  "form.adultsAge": "13+ yrs",
  "form.childrenAge": "4–12 yrs",
  "form.toddlersAge": "0–3 yrs",
  "form.free": "Free",
  "form.name": "Name",
  "form.namePlaceholder": "Your full name",
  "form.email": "Email",
  "form.phone": "Phone (optional)",
  "form.notes": "Notes (optional)",
  "form.notesPlaceholder": "Dietary needs, accessibility, preferences...",
  "form.settingUpPayment": "Setting up payment...",
  "form.continueToPayment": "Continue to payment",
  "form.minGuests": "Minimum {count} guests required",
  "form.maxGuests": "Maximum {count} guests for this slot",
  // Cart
  "cart.title": "Your cart",
  "cart.empty": "Your cart is empty",
  "cart.browseExperiences": "Browse experiences",
  "cart.estimatedTotal": "Estimated total",
  "cart.dynamicPriceNote": "Final price calculated at checkout including any dynamic pricing.",
  "cart.proceedToCheckout": "Proceed to checkout",
  "cart.itemRemoved": "Item removed from cart",
  "cart.removeFailed": "Failed to remove item",
  // Checkout extras
  "checkout.guests": "{count} guest|{count} guests",
  // Vision presentation - Intro
  "vision.s0.headline": "Let the world wait.",
  "vision.s0.subline": "Thoughtfully chosen moments, guided by people who know this place.",
  "vision.s0.cta": "Let's dive in to the experiences",
  // Vision – Origin (Pixar: Why must you tell this story?)
  "vision.origin.eyebrow": "The Belief",
  "vision.origin.headline": "I've seen what happens when someone feels at home.",
  "vision.origin.p1": "I've watched a relocated engineer sit alone in a perfectly furnished apartment, scrolling through a city she doesn't recognise. Everything was handled — the lease, the furniture, the WiFi password. But nobody handled the loneliness.",
  "vision.origin.p2": "That moment stays with me. Because we built a company around the idea of home. And home is not a floor plan. Home is the moment you stop feeling like a stranger.",
  "vision.origin.p3": "This is why Guestly Experiences exists. Not as a feature. Not as a revenue line. As a conviction: that the space between arrival and belonging is ours to close.",
  "vision.origin.signature": "— Joakim, Co-founder",
  // Vision presentation
  "vision.s1.eyebrow": "Why Now",
  "vision.s1.headline": "The market has shifted.",
  "vision.s1.stat1.value": "$1T+",
  "vision.s1.stat1.label": "Total experiences market",
  "vision.s1.stat2.value": "$250–310B",
  "vision.s1.stat2.label": "Paid & structured segment",
  "vision.s1.stat3.value": "$250M",
  "vision.s1.stat3.label": "Airbnb investing in Experiences",
  "vision.s1.stat4.value": "+12%",
  "vision.s1.stat4.label": "Airbnb Q4 revenue growth",
  "vision.s1.stat5.value": "+16%",
  "vision.s1.stat5.label": "GBV year-over-year",
  "vision.s1.stat6.value": "+10%",
  "vision.s1.stat6.label": "Nights & Seats booked",
  "vision.s1.stat7.value": "~50%",
  "vision.s1.stat7.label": "Experience bookings not tied to stays",
  "vision.s1.stat8.value": "<1%",
  "vision.s1.stat8.label": "Top player market share (Groupon)",
  "vision.s1.closing": "Accommodation is no longer a cost line. It's a performance lever.",
  "vision.s2.eyebrow": "The Gap",
  "vision.s2.headline": "What's missing today?",
  "vision.s2.pain1": "Relocated employees report loneliness and disorientation in the first two weeks (BGRS / Mercer)",
  "vision.s2.pain2": "72% of corporate travelers say local recommendations would improve their trip (GBTA)",
  "vision.s2.pain3": "Bookers manage 40+ suppliers per city and still can't guarantee the guest experience (CWT)",
  "vision.s2.pain4": "Guests want structure without restriction — a rhythm, not a schedule",
  "vision.s2.pain5": "One bad relocation experience costs the employer 3× the assignment value (ECA International)",
  "vision.s2.tension": "We deliver the apartment. We haven't delivered belonging.",
  // Vision – Stakes (Pixar: What are the stakes?)
  "vision.stakes.eyebrow": "What's at Stake",
  "vision.stakes.headline": "If we don't act, someone else will.",
  "vision.stakes.p1": "Airbnb is investing $250M to rebuild Experiences. GetYourGuide just raised $194M. Klook, Viator, and Groupon are circling the same guests we already house.",
  "vision.stakes.p2": "If we stay passive, our guests will book experiences from platforms that don't know them, don't care about them, and don't share our standards. Our brand becomes wallpaper — functional, forgettable.",
  "vision.stakes.block1.label": "If we do nothing",
  "vision.stakes.block1.line1": "Guests leave without connection",
  "vision.stakes.block1.line2": "Bookers see us as interchangeable",
  "vision.stakes.block1.line3": "Competitors own the guest relationship",
  "vision.stakes.block1.line4": "The brand erodes to a logistics function",
  "vision.stakes.block2.label": "If we move now",
  "vision.stakes.block2.line1": "Guests remember us, not just our apartments",
  "vision.stakes.block2.line2": "Bookers choose us because we deliver more",
  "vision.stakes.block2.line3": "We own the full guest journey",
  "vision.stakes.block2.line4": "The brand compounds with every stay",
  "vision.stakes.closing": "The window is open. First movers in curated, local-first experiences will define the category.",
  "vision.s3.eyebrow": "Strategic Opportunity",
  "vision.s3.headline": "Guestly Experiences",
  "vision.s3.pillar1": "Retention tool",
  "vision.s3.pillar2": "Brand amplifier",
  "vision.s3.pillar3": "Revenue enhancer",
  "vision.s3.pillar4": "Differentiator",
  "vision.s3.pillar5": "Ecosystem builder",
  "vision.s3.insight1": "Airbnb now targeting local residents, not just travelers — 85% of market could be non-tourist",
  "vision.s3.insight2": "Fragmented market rewards curated, local-first platforms",
  "vision.s3.insight3": "Airbnb Services shows accommodation + experiences convergence is inevitable",
  "vision.s3.quote": "Experiences are not about corporate housing & tourism. They are about performance, belonging, and brand memory.",
  "vision.s4.eyebrow": "The Model",
  "vision.s4.headline": "How it works",
  "vision.s4.layerLabel": "Layer",
  "vision.s4.layer1.title": "Curation",
  "vision.s4.layer1.desc": "Selected providers per city, 7-step quality onboarding, 80% quiz pass rate",
  "vision.s4.layer2.title": "Integration",
  "vision.s4.layer2.desc": "Boarding Pass, SMS nudges, in-apartment QR codes, email flows",
  "vision.s4.layer3.title": "Monetization",
  "vision.s4.layer3.desc": "Revenue share, affiliate, loyalty incentives, corporate bundles",
  "vision.s4.layer4.title": "Data",
  "vision.s4.layer4.desc": "Guest choices, engagement timing, repeat stay drivers",
  "vision.s4.flywheel": "Flywheel",
  "vision.s4.fw1": "Stay",
  "vision.s4.fw2": "Experience",
  "vision.s4.fw3": "Memory",
  "vision.s4.fw4": "Review",
  "vision.s4.fw5": "Repeat",
  "vision.s4.fw6": "Booker & Guest confidence",
  "vision.s4.fw7": "Portfolio growth",
  "vision.s5.eyebrow": "Financial Impact",
  "vision.s5.headline": "Measurable return",
  "vision.s5.card1.value": "NPS ↑",
  "vision.s5.card1.desc": "Higher repeat booking rate through enriched guest experience",
  "vision.s5.card2.value": "B2B ✦",
  "vision.s5.card2.desc": "Stronger corporate client retention vs commodity competitors",
  "vision.s5.card3.value": "1.5–2.0 MSEK",
  "vision.s5.card3.desc": "Added to bottom line for Guestly Homes through incremental experience revenue, repeat stay uplift, and B2B retention",
  "vision.s5.detail1": "Bernstein: Services could add 4-5% to total revenues if supply is onboarded",
  "vision.s5.detail2": "Airbnb's goal: new businesses doing $1B/year in revenue",
  "vision.s5.detail3": "If we increase repeat stays by X%, portfolio value shifts by Y.",
  "vision.s6.eyebrow": "Risk Analysis",
  "vision.s6.headline": "Controlled execution",
  "vision.s6.riskLabel": "Risk",
  "vision.s6.mitigationLabel": "Mitigation",
  "vision.s6.risk1.title": "Brand dilution",
  "vision.s6.risk1.mitigation": "Curated providers, 7-step quality review, 80% quiz pass rate",
  "vision.s6.risk2.title": "Operational burden",
  "vision.s6.risk2.mitigation": "Fully automated integration, no manual coordination needed",
  "vision.s6.risk3.title": "Core business distraction",
  "vision.s6.risk3.mitigation": "Embedded into existing guest journey, not a separate product",
  "vision.s6.closing": "This is not expansion. It's depth.",
  // Vision – Essence (Pixar: Most economical telling)
  "vision.essence.eyebrow": "The Essence",
  "vision.essence.line1": "A guest arrives.",
  "vision.essence.line2": "The apartment is ready.",
  "vision.essence.line3": "But something is missing.",
  "vision.essence.line4": "We fill that gap — with a local guide, a shared meal, a quiet paddle at dawn.",
  "vision.essence.line5": "The guest leaves. But the memory stays.",
  "vision.essence.line6": "They come back. Not for the apartment. For how it made them feel.",
  "vision.essence.closing": "That's the whole story. Everything else is how we make it real.",
  "vision.s7.why": "We exist so people feel at home, wherever they are.",
  "vision.s7.how": "Through systems that remove friction and anticipate needs.",
  "vision.s7.what": "Today, we provide housing. Tomorrow, we provide belonging.",
  "vision.s7.ifSoLabel": "If so",
  "vision.s7.ifSo": "Then every stay becomes a story. Every city becomes familiar. Every guest becomes an advocate.",
  "vision.s7.futureGuests": "Guests",
  "vision.s7.futureBookers": "Bookers",
  "vision.s7.futureTeam": "Guestly Homes Team",
  "vision.s7.futurePartners": "Partners",
  "vision.s7.future.guests": "Richer stays. Local connections. Memories that outlast the trip.",
  "vision.s7.future.bookers": "Zero friction. Trusted quality. A partner, not a vendor.",
  "vision.s7.future.partners": "A curated platform. Qualified leads. A brand worth being part of.",
  "vision.s7.future.team": "Deeper moat. Stronger retention. A brand people remember.",
  "vision.s7.close": "Let the world wait.",
  // Partner page
  "partner.nav.cta": "Register now",
  "partner.hero.headline.A": "Your next 3,000 guests are waiting",
  "partner.hero.headline.B": "Grow your experience business with Guestly",
  "partner.hero.subline": "Reach high-intent guests staying in our homes. Get tools, payouts, and support built for quality operators.",
  "partner.hero.cta.A": "Register now",
  "partner.hero.cta.B": "Get started free",
  "partner.hero.secondaryCta": "See requirements",
  "partner.hero.proof.0": "Local guests",
  "partner.hero.proof.1": "Fast payouts",
  "partner.hero.proof.2": "No upfront cost",
  "partner.whyPartner.title": "Why partner with us?",
  "partner.whyPartner.subtitle": "Everything you need to grow your experience business.",
  "partner.whyPartner.0.title": "Expand your reach",
  "partner.whyPartner.0.desc": "Get discovered by guests staying in premium Guestly Homes across Sweden.",
  "partner.whyPartner.1.title": "Maximize revenue",
  "partner.whyPartner.1.desc": "Dynamic pricing, add-ons, and bundle options to increase your average booking value.",
  "partner.whyPartner.2.title": "No upfront costs",
  "partner.whyPartner.2.desc": "Commission-based model. You only pay when you earn.",
  "partner.whyPartner.3.title": "Showcase your quality",
  "partner.whyPartner.3.desc": "Join a curated community where quality partners stand out.",
  "partner.whyPartner.4.title": "Grow with confidence",
  "partner.whyPartner.4.desc": "Dedicated support, clear standards, and tools to manage your business.",
  "partner.whyPartner.5.title": "Keep your systems",
  "partner.whyPartner.5.desc": "Use your existing calendar tools and booking systems. Integration comes later.",
  "partner.testimonials.0.quote": "Guestly brought us guests we'd never have reached on our own. The quality of bookings is outstanding.",
  "partner.testimonials.0.name": "Erik Lindström",
  "partner.testimonials.0.role": "Founder",
  "partner.testimonials.0.company": "Arctic Kayak Adventures",
  "partner.testimonials.1.quote": "The dashboard and payout system make everything transparent. I know exactly what I earn from each booking.",
  "partner.testimonials.1.name": "Maria Johansson",
  "partner.testimonials.1.role": "Owner",
  "partner.testimonials.1.company": "West Coast Food Tours",
  "partner.testimonials.2.quote": "Professional, reliable, and they actually care about quality. The onboarding was smooth and supportive.",
  "partner.testimonials.2.name": "Anders Bergqvist",
  "partner.testimonials.2.role": "Instructor",
  "partner.testimonials.2.company": "Lapland Wilderness School",
  "partner.stats.title": "Trusted by quality operators across Sweden",
  "partner.stats.0.value": "50,000+",
  "partner.stats.0.label": "Monthly guests in our homes",
  "partner.stats.1.value": "12",
  "partner.stats.1.label": "Experience categories",
  "partner.stats.2.value": "8",
  "partner.stats.2.label": "Cities in Sweden",
  "partner.stats.3.value": "4",
  "partner.stats.3.label": "Languages supported",
  "partner.tools.title": "Tools to grow your business",
  "partner.tools.subtitle": "Everything you need to manage and scale your experiences.",
  "partner.tools.0.title": "Analytics dashboard",
  "partner.tools.0.desc": "Track bookings, revenue, and guest trends in real-time. Know what works.",
  "partner.tools.1.title": "AI-assisted listings",
  "partner.tools.1.desc": "Get draft descriptions and compliance hints powered by AI. Launch faster.",
  "partner.tools.2.title": "Demand insights",
  "partner.tools.2.desc": "See availability suggestions and demand spikes. Optimize your schedule.",
  "partner.howToStart.title": "Get started in 3 steps",
  "partner.howToStart.subtitle": "From signup to your first booking — it's straightforward.",
  "partner.howToStart.0.title": "Create your account",
  "partner.howToStart.0.desc": "Register with your business details. Takes about 2 minutes.",
  "partner.howToStart.1.title": "Submit your experience",
  "partner.howToStart.1.desc": "Add your activity details, photos, and pricing for review.",
  "partner.howToStart.2.title": "Publish and receive requests",
  "partner.howToStart.2.desc": "Once approved, guests can discover and book your experience.",
  "partner.howItWorks.title": "How it works",
  "partner.howItWorks.subtitle": "A simple model that lets you focus on what you do best.",
  "partner.howItWorks.0.title": "We bring the guests",
  "partner.howItWorks.0.desc": "Guestly guests discover your experience through our platform and homes.",
  "partner.howItWorks.1.title": "You deliver the experience",
  "partner.howItWorks.1.desc": "Run your activity as you always do. We handle the booking flow.",
  "partner.howItWorks.2.title": "We handle the rest",
  "partner.howItWorks.2.desc": "Payments, receipts, and guest communication — all managed for you.",
  "partner.requirements.title": "What we expect from experience partners",
  "partner.requirements.subtitle": "We maintain high standards to protect guests and partners alike.",
  "partner.requirements.0": "Registered business or registered individual (F-skatt in Sweden)",
  "partner.requirements.1": "Verified identity and contact details",
  "partner.requirements.2": "Business liability insurance",
  "partner.requirements.3": "Documentation for high-risk activities (licenses, permits, instructor certifications)",
  "partner.requirements.4": "High-quality images and clear guest instructions",
  "partner.requirements.5": "Safety plan and cancellation policy",
  "partner.requirements.notSure.title": "Not sure if you qualify?",
  "partner.requirements.notSure.desc": "Most operators do. If you run a legitimate experience business with proper insurance, you're likely a great fit.",
  "partner.requirements.notSure.cta": "Talk to us",
  "partner.faq.title": "Frequently asked questions",
  "partner.faq.0.q": "What is Guestly Experiences?",
  "partner.faq.0.a": "Guestly Experiences is a curated community connecting quality experience providers with guests staying in Guestly Homes across Sweden.",
  "partner.faq.1.q": "Who can register?",
  "partner.faq.1.a": "Any registered business or individual offering tours, activities, or experiences in Sweden. You need proper insurance and any required licenses for your activity type.",
  "partner.faq.2.q": "Will my activity be accepted?",
  "partner.faq.2.a": "We review each submission for quality, safety, and guest suitability. Most legitimate operators are approved within 5 business days.",
  "partner.faq.3.q": "What does it cost?",
  "partner.faq.3.a": "There are no upfront costs. We charge a commission on confirmed bookings only. You keep the majority of each booking.",
  "partner.faq.4.q": "How do payouts work?",
  "partner.faq.4.a": "Payouts are processed automatically to your Stripe Connect account and occur directly after the experience or activity is completed. You can track all earnings in your provider dashboard.",
  "partner.faq.5.q": "What commission do you charge?",
  "partner.faq.5.a": "Commission rates are discussed during onboarding and depend on your activity type and monthly volume. Contact us for details.",
  "partner.faq.6.q": "What happens after I sign up?",
  "partner.faq.6.a": "After email verification, you'll complete a short onboarding flow: business details, documentation upload, and a brief quality quiz. Then you can submit your first experience.",
  "partner.faq.7.q": "Can I keep my own booking system?",
  "partner.faq.7.a": "Yes. We don't require you to replace your existing tools. Calendar integration options will be available in future updates.",
  "partner.faq.8.q": "What documents are required for motorized or high-risk activities?",
  "partner.faq.8.a": "You'll need to provide valid permits, instructor certifications, equipment safety records, and proof of adequate insurance coverage for your specific activity type.",
  "partner.finalCta.title": "Submit your experiences today",
  "partner.finalCta.subtitle": "Create your account in minutes.",
  "partner.finalCta.cta": "Register now",
  "partner.modal.title": "Start your provider application",
  "partner.modal.desc": "Tell us a bit about your business. We'll guide you through the rest.",
  "partner.modal.emailLabel": "Email address",
  "partner.modal.countryLabel": "Country",
  "partner.modal.businessTypeLabel": "Business type",
  "partner.modal.cta": "Continue",
  "partner.modal.microcopy": "We'll email you a verification link after registration.",
  "partner.modal.businessType.company": "Company",
  "partner.modal.businessType.individual": "Individual",
  "partner.modal.businessType.other": "Other",
  "partner.footer.browseExperiences": "Browse experiences",
  // Registration flow
  "partner.register.step1.title": "Join us as a partner",
  "partner.register.step1.subtitle": "How do you run your business?",
  "partner.register.step1.type.company": "Registered company",
  "partner.register.step1.type.company.sub": "AB, HB, or equivalent",
  "partner.register.step1.type.individual": "Registered individual",
  "partner.register.step1.type.individual.sub": "Enskild firma / F-skatt",
  "partner.register.step1.type.nonprofit": "Non-profit organization",
  "partner.register.step1.type.nonprofit.sub": "Ideell förening",
  "partner.register.step1.type.government": "Government entity",
  "partner.register.step1.type.government.sub": "Municipal or state body",
  "partner.register.step1.type.education": "Educational institution",
  "partner.register.step1.type.education.sub": "School, university",
  "partner.register.step1.type.other": "Other business type",
  "partner.register.step1.type.other.sub": "Tell us more later",
  "partner.register.step1.login": "Already have an account?",
  "partner.register.step1.loginLink": "Log in",
  "partner.register.step2.title": "Tell us about your business",
  "partner.register.step2.activityCountLabel": "How many activities do you offer?",
  "partner.register.step2.reservationLabel": "Do you use a reservation system to manage your services & experiences?",
  "partner.register.step2.yes": "Yes",
  "partner.register.step2.no": "No",
  "partner.register.step3.title": "Create your account",
  "partner.register.step3.infoBanner": "You'll receive an email to verify your account after registering.",
  "partner.register.step3.brandLabel": "Public / brand name",
  "partner.register.step3.brandPlaceholder": "e.g. Arctic Kayak Adventures",
  "partner.register.step3.websiteLabel": "Website",
  "partner.register.step3.websitePlaceholder": "https://your-website.com",
  "partner.register.step3.countryLabel": "Country registered",
  "partner.register.step3.currencyLabel": "Preferred payout currency",
  "partner.register.step3.firstNameLabel": "First name",
  "partner.register.step3.lastNameLabel": "Last name",
  "partner.register.step3.emailLabel": "Email",
  "partner.register.step3.passwordLabel": "Password",
  "partner.register.step3.termsText": "I have read and agree to the",
  "partner.register.step3.termsLink": "Supplier Terms",
  "partner.register.step3.privacyLink": "Privacy Policy",
  "partner.register.step3.termsAnd": "and",
  "partner.register.step3.cta": "Create an account",
  "partner.register.checkInbox.title": "Check your inbox",
  "partner.register.checkInbox.desc": "We've sent a verification email to {email}. Click the link to activate your account and start onboarding.",
  "partner.register.checkInbox.spamNote": "Didn't receive it? Check your spam folder or",
  "partner.register.checkInbox.resendLink": "resend the email",
  "partner.register.noEmail.title": "Start your application",
  "partner.register.noEmail.desc": "Please start from the partner page to begin your registration.",
  "partner.register.noEmail.cta": "Go to partner page",
  "partner.register.continue": "Continue",
  "partner.register.back": "Back",
  "partner.register.stepOf": "Step {current} of {total}",
  "partner.register.password.length": "8–30 characters",
  "partner.register.password.number": "At least one number",
  "partner.register.password.special": "At least one special character",
  "partner.register.password.case": "Upper and lowercase letters",
  "partner.register.password.spaces": "No spaces",
  "partner.register.password.email": "Doesn't contain your email",
  // Legal — Supplier Terms
  "legal.terms.title": "Supplier Terms & Conditions",
  "legal.terms.version": "Version 1.0 — Effective from January 2025",
  "legal.terms.s1.title": "1. Scope of Agreement",
  "legal.terms.s1.body": "These Supplier Terms govern the relationship between you (\"Provider\") and Guestly Experiences AB (\"Guestly\") when you list and deliver experiences through the Guestly Experiences platform. By creating an account, you agree to these terms in full.",
  "legal.terms.s2.title": "2. Commission & Pricing",
  "legal.terms.s2.body": "Guestly charges a commission on each confirmed booking, as agreed during your onboarding. You set your own net prices; Guestly applies a markup to determine the guest-facing price. Commission rates may be updated with 30 days' written notice.",
  "legal.terms.s3.title": "3. Payout Terms",
  "legal.terms.s3.body": "Payouts are processed monthly to the bank account registered in your provider dashboard. All payouts are made in your selected payout currency. Guestly reserves the right to withhold payouts in case of disputes, chargebacks, or policy violations until resolved.",
  "legal.terms.s4.title": "4. Provider Obligations",
  "legal.terms.s4.item1": "Maintain valid business registration and required licenses for your activity type.",
  "legal.terms.s4.item2": "Hold adequate business liability insurance covering all listed experiences.",
  "legal.terms.s4.item3": "Comply with all applicable local, national, and EU safety regulations.",
  "legal.terms.s4.item4": "Provide accurate descriptions, images, and availability information.",
  "legal.terms.s4.item5": "Respond to booking requests within 24 hours.",
  "legal.terms.s4.item6": "Deliver the experience as described in the listing.",
  "legal.terms.s5.title": "5. Cancellation & Refunds",
  "legal.terms.s5.body": "Guests may cancel free of charge up to the cancellation deadline specified on your listing. Provider-initiated cancellations must be communicated to Guestly immediately. Repeated provider cancellations may result in listing suspension or account termination.",
  "legal.terms.s6.title": "6. Intellectual Property",
  "legal.terms.s6.body": "You grant Guestly a non-exclusive, royalty-free license to use your listing content (text, images, logos) for marketing and promotional purposes on the Guestly platform and associated channels. You retain ownership of your original content.",
  "legal.terms.s7.title": "7. Limitation of Liability",
  "legal.terms.s7.body": "Guestly acts as a platform intermediary and is not liable for the execution of experiences. The Provider assumes full responsibility for guest safety, activity delivery, and compliance with applicable laws. Guestly's total liability shall not exceed the commissions earned from the Provider in the preceding 12 months.",
  "legal.terms.s8.title": "8. Termination",
  "legal.terms.s8.body": "Either party may terminate this agreement with 30 days' written notice. Guestly may suspend or terminate your account immediately in case of material breach, safety concerns, or repeated guest complaints. Upon termination, all pending payouts for completed experiences will be processed.",
  "legal.terms.s9.title": "9. Governing Law",
  "legal.terms.s9.body": "These terms are governed by Swedish law. Any disputes shall be resolved by the Swedish courts.",
  // Legal — Privacy Policy
  "legal.privacy.title": "Privacy Policy",
  "legal.privacy.version": "Version 1.0 — Effective from January 2025",
  "legal.privacy.s1.title": "1. Data Controller",
  "legal.privacy.s1.body": "Guestly Experiences AB (org. nr pending), registered in Sweden, is the data controller for all personal data processed through the Guestly Experiences platform. For questions about your data, contact privacy@guestly.se.",
  "legal.privacy.s2.title": "2. What Data We Collect",
  "legal.privacy.s2.item1": "Account data: Name, email, phone number, business registration details.",
  "legal.privacy.s2.item2": "Financial data: Bank account details for payouts, transaction history.",
  "legal.privacy.s2.item3": "Listing data: Experience descriptions, images, pricing, availability.",
  "legal.privacy.s2.item4": "Usage data: Login activity, dashboard interactions, analytics events.",
  "legal.privacy.s2.item5": "Communication data: Messages exchanged through the platform.",
  "legal.privacy.s3.title": "3. Legal Basis for Processing",
  "legal.privacy.s3.item1": "Contract performance: Processing necessary to fulfil our agreement with you.",
  "legal.privacy.s3.item2": "Legitimate interest: Platform improvement, fraud prevention, analytics.",
  "legal.privacy.s3.item3": "Legal obligation: Tax reporting, regulatory compliance.",
  "legal.privacy.s3.item4": "Consent: Marketing communications (opt-in).",
  "legal.privacy.s4.title": "4. Data Sharing",
  "legal.privacy.s4.intro": "We may share your data with:",
  "legal.privacy.s4.item1": "Guests: Your public listing information and host name.",
  "legal.privacy.s4.item2": "Payment processors: Stripe, for payment processing and payouts.",
  "legal.privacy.s4.item3": "Cloud infrastructure: Hosting and database providers (EU-based).",
  "legal.privacy.s4.item4": "Legal authorities: When required by law or regulation.",
  "legal.privacy.s4.noSale": "We do not sell your personal data to third parties.",
  "legal.privacy.s5.title": "5. Data Retention",
  "legal.privacy.s5.body": "Account data is retained for the duration of your provider agreement plus 24 months. Financial records are retained for 7 years as required by Swedish accounting law. You may request deletion of non-mandatory data at any time.",
  "legal.privacy.s6.title": "6. Your Rights (GDPR)",
  "legal.privacy.s6.intro": "As a data subject under GDPR, you have the right to:",
  "legal.privacy.s6.item1": "Access your personal data and receive a copy.",
  "legal.privacy.s6.item2": "Rectify inaccurate or incomplete data.",
  "legal.privacy.s6.item3": "Request erasure of your data (\"right to be forgotten\").",
  "legal.privacy.s6.item4": "Restrict or object to processing.",
  "legal.privacy.s6.item5": "Data portability — receive your data in a structured format.",
  "legal.privacy.s6.item6": "Lodge a complaint with the Swedish Authority for Privacy Protection (IMY).",
  "legal.privacy.s7.title": "7. Contact",
  "legal.privacy.s7.body": "For any data-related inquiries, contact us at privacy@guestly.se.",
  // Legal — DPA
  "legal.privacy.s8.title": "8. Data Processing Agreement (DPA)",
  "legal.privacy.s8.intro": "This section constitutes a Data Processing Agreement between Guestly Experiences AB (\"Processor\") and the Provider (\"Controller\") in accordance with Article 28 of the General Data Protection Regulation (GDPR).",
  "legal.privacy.s8.scope.title": "Scope",
  "legal.privacy.s8.scope.body": "Guestly processes personal data on behalf of the Provider for the purpose of managing bookings, guest communication, and related platform operations.",
  "legal.privacy.s8.purpose.title": "Processing Purpose",
  "legal.privacy.s8.purpose.body": "Data is processed solely for fulfilling bookings, sending notifications, generating analytics, and providing platform functionality as described in the main agreement.",
  "legal.privacy.s8.subprocessors.title": "Sub-processors",
  "legal.privacy.s8.subprocessors.body": "Guestly uses vetted sub-processors in the following categories: cloud hosting (EU-based), payment processing (Stripe), email delivery (Postmark), and SMS notifications (Twilio). A current list of sub-processors is available upon request.",
  "legal.privacy.s8.security.title": "Data Security",
  "legal.privacy.s8.security.body": "All personal data is encrypted at rest and in transit. Access is restricted to authorized personnel on a need-to-know basis. Guestly maintains appropriate technical and organizational measures to protect personal data.",
  "legal.privacy.s8.breach.title": "Data Breach Notification",
  "legal.privacy.s8.breach.body": "In the event of a personal data breach, Guestly will notify the Provider without undue delay and no later than 72 hours after becoming aware of the breach, in accordance with Article 33 of the GDPR.",
  "legal.privacy.s8.duration.title": "Duration",
  "legal.privacy.s8.duration.body": "This DPA is co-terminus with the provider agreement. Upon termination, all personal data will be deleted or returned within 30 days, unless retention is required by law.",
  "legal.privacy.s8.rights.title": "Provider Rights",
  "legal.privacy.s8.rights.body": "The Provider has the right to request audit information with reasonable notice. Guestly will provide documentation of its data processing activities and security measures upon written request.",
  // Book a Home CTA
  "detail.bookHomeHeading": "Need a place to stay?",
  "detail.bookHomeBody": "Browse verified homes in {city} — managed by Guestly Homes.",
  "detail.bookHomeCta": "Browse homes in {city}",
  // AI Search
  "search.placeholder": "What kind of experience are you looking for?",
  // Card badges
  "card.topRated": "Top rated",
  "card.certified": "Certified",
  "card.person": "person",
  "card.group": "group",
  // Date filter
  "filter.date": "Date",
  // Top destinations
  "destinations.heading": "Top destinations",
  // Go-live checklist
  "portal.goLive.heading": "Ready to go live?",
  "portal.goLive.desc": "Complete these items before your experience can be published.",
  "portal.goLive.title": "Experience title",
  "portal.goLive.description": "Description",
  "portal.goLive.images": "At least 3 images",
  "portal.goLive.pricing": "Pricing set",
  "portal.goLive.slot": "At least 1 time slot",
  "portal.goLive.meetingPoint": "Meeting point",
   "portal.goLive.included": "What's included",
   "portal.goLive.payouts": "Payout method connected",
   "portal.goLive.allDone": "All set! You can request publishing.",
  // Footer
  "footer.destinations": "Destinations",
  "footer.categories": "Categories",
  "footer.company": "Company",
  "footer.connect": "Connect",
  "footer.about": "About",
  "footer.privacy": "Privacy Policy",
  "footer.terms": "Terms",
  "footer.forHosts": "For hosts",
  "footer.affiliateProgram": "Affiliate Program",
  "footer.contact": "Contact",
  "footer.tagline": "Curated experiences, without friction.",
  "footer.taglineSub": "Plan less. Arrive ready.",
  "footer.explore": "Explore",
  "footer.allExperiences": "All experiences",
  "footer.destinationsDirectory": "Destinations",
  "footer.giftCards": "Gift cards",
  "footer.blog": "Blog",
  "footer.support": "Support",
  "footer.helpFaq": "Help & FAQ",
  "footer.cancellation": "Cancellation & refunds",
  "footer.trustLegal": "Trust & Legal",
  "footer.cookieSettings": "Cookie settings",
  "footer.sitemap": "Sitemap",
  "footer.accessibility": "Accessibility",
  "footer.viewAllDestinations": "View all destinations",
"footer.createdIn": "Crafted with ❤️ in Norrbotten, Sweden",
  "footer.trademark": "Guestly Experiences is a trademark owned by Guestly Homes Sweden AB.",
  "footer.careers": "Careers",
  "footer.press": "Press",
  // Destination pages
  "destination.experiencesIn": "Experiences in {city}",
  "destination.moreDestinations": "More destinations",
  // Destinations directory
  "directory.heading": "Destinations",
  "directory.subline": "Explore curated local experiences across Sweden.",
  // Destination FAQ
  "destination.faq.heading": "Frequently asked questions",
  "destination.faq.q1": "What experiences are available in {city}?",
  "destination.faq.a1": "We offer a curated selection of local experiences in {city}, including nature, culture, and adventure activities — all guided by vetted local hosts.",
  "destination.faq.q2": "How do I book an experience in {city}?",
  "destination.faq.a2": "Browse the experiences listed above, pick your preferred date and group size, and submit a booking request. You'll receive confirmation via email.",
  "destination.faq.q3": "Can I cancel my booking?",
  "destination.faq.a3": "Yes. Most experiences offer free cancellation up to 7 days before the activity. See each listing for exact terms.",
  // About page
  "about.heading": "About Guestly Experiences",
  "about.intro": "We believe the space between arrival and belonging is ours to close.",
  "about.body1": "Guestly Experiences connects guests with carefully selected local hosts — people who know this place deeply and share it with care. Every experience goes through a multi-step quality review before it goes live.",
  "about.companyHeading": "Company",
  "about.companyName": "Guestly Homes Sweden AB",
  "about.orgNr": "Org. nr: 559359-3238",
  "about.address": "944 91 Hortlax, Sweden",
  "about.founderHeading": "Founded by",
  "about.founderName": "Joakim Thörn",
  "about.founderBio": "Co-founder Guestly Homes. Driven by the conviction that travel should be more than logistics. Previously built Guestly Homes into one of Sweden's leading short-, mid- and long-term rental operators.",
  "about.closing": "No checklists. No hurrying through. Just moments that hold you.",
  // Help Center
  "help.heading": "How can we help?",
  "help.searchPlaceholder": "Search for help…",
  "help.searchButton": "Search",
  "help.viewingProvider": "Viewing help for experiences by",
  "help.backToExperiences": "Back to experiences",
  "help.topic.booking.title": "Booking & Cancellation",
  "help.topic.booking.desc": "How to book, modify, or cancel an experience.",
  "help.topic.payment.title": "Payment",
  "help.topic.payment.desc": "Accepted methods, refunds, and receipts.",
  "help.topic.safety.title": "Safety",
  "help.topic.safety.desc": "Insurance, gear requirements, and age limits.",
  "help.topic.groups.title": "Group Bookings",
  "help.topic.groups.desc": "Private tours, large parties, and custom requests.",
  "help.topic.contact.title": "Contact Provider",
  "help.topic.contact.desc": "Reach your experience host directly.",
  "help.topic.general.title": "General",
  "help.topic.general.desc": "Account, accessibility, and other questions.",
  "help.still.heading": "Still need help?",
  "help.still.body": "Our team is here to help. Reach out and we'll get back to you within 24 hours.",
  "help.defaultAnswer": "For specific questions, please email us at experiences@guestly.com. We typically respond within 24 hours.",
  "help.subheading": "Ask anything about your booking, the experience, or Guestly policies.",
  "help.context.by": "by",
  "help.context.freeCancel": "Free cancel {0}h before",
  "help.error": "Something went wrong. Please try again.",
  "help.topic.booking.prompt": "How do I cancel or change my booking?",
  "help.topic.payment.prompt": "How does payment work and what methods are accepted?",
  "help.topic.safety.prompt": "What safety measures are in place for this experience?",
  "help.topic.groups.prompt": "What are the group size limits and can I bring children?",
  "help.topic.contact.prompt": "How can I contact the experience provider?",
  "help.topic.general.prompt": "What should I know before booking this experience?",
  // Help Center — Contact Provider form
  "help.contact.heading": "Send a message to the provider",
  "help.contact.subheading": "Your message will be forwarded to {provider} via Guestly.",
  "help.contact.subheadingGeneric": "Your message will be forwarded to the experience provider via Guestly.",
  "help.contact.regarding": "Regarding:",
  "help.contact.name": "Your name",
  "help.contact.namePlaceholder": "Jane Doe",
  "help.contact.email": "Your email",
  "help.contact.emailPlaceholder": "jane@example.com",
  "help.contact.message": "Message",
  "help.contact.messagePlaceholder": "Write your question or message here…",
  "help.contact.send": "Send message",
  "help.contact.sentTitle": "Message sent!",
  "help.contact.sentBody": "We've forwarded your message to the provider. They'll reply directly to your email.",
  "help.contact.error": "Could not send your message. Please try again.",
  // Privacy page (guest-facing)
  "privacy.heading": "Privacy Policy",
  "privacy.version": "Version 1.0 — Effective 1 March 2025",
  "privacy.s1.title": "1. Who we are",
  "privacy.s1.body": "Guestly Homes Sweden AB, org. nr 559359-3238, 944 91 Hortlax, Sweden. We are the data controller for the personal data processed through the Guestly Experiences community at guestlyexperiences.com.",
  "privacy.s2.title": "2. What we collect",
  "privacy.s2.intro": "We collect the following when you use our community:",
  "privacy.s2.booking": "Booking details — name, email, phone number, number of guests, date, and any information required by the experience listing.",
  "privacy.s2.payment": "Payment information — processed by our payment provider. We do not store your full card details.",
  "privacy.s2.reviews": "Reviews — any content you submit, including text, photos, and your name.",
  "privacy.s2.technical": "Technical data — IP address, browser type, device information, and usage data collected automatically when you visit our site.",
  "privacy.s2.comms": "Communications — messages you send to us via email or contact forms.",
  "privacy.s3.title": "3. Why we use your data",
  "privacy.s3.intro": "We process your personal data to:",
  "privacy.s3.fulfil": "Fulfil bookings — share necessary details with Hosts so your experience can take place (contract performance).",
  "privacy.s3.payments": "Process payments — handle transactions securely through our payment provider (contract performance).",
  "privacy.s3.communicate": "Communicate with you — send booking confirmations, updates, and respond to enquiries (contract performance).",
  "privacy.s3.improve": "Improve our community — understand how our site is used and make it better (legitimate interest).",
  "privacy.s3.marketing": "Send marketing — only with your consent, and you can withdraw it at any time.",
  "privacy.s3.legal": "Meet legal obligations — comply with applicable laws, including tax and accounting requirements.",
  "privacy.s4.title": "4. Who we share your data with",
  "privacy.s4.intro": "We share your data only when necessary:",
  "privacy.s4.hosts": "Hosts — the details they need to deliver your experience.",
  "privacy.s4.stripe": "Payment provider (Stripe) — to process your payment securely.",
  "privacy.s4.postmark": "Email provider (Postmark) — to send booking confirmations and communications.",
  "privacy.s4.hosting": "Hosting and infrastructure — to operate our community securely.",
  "privacy.s4.noSell": "We never sell your personal data.",
  "privacy.s4.transfers": "Some of our service providers are based outside the EU/EEA (primarily the USA). Where this is the case, we ensure appropriate safeguards are in place, such as the EU-US Data Privacy Framework or standard contractual clauses.",
  "privacy.s5.title": "5. How long we keep your data",
  "privacy.s5.body": "We retain your data for as long as necessary to fulfil the purposes described above or as required by law. Booking records are kept for the period required by Swedish accounting law (currently seven years). You can request deletion at any time — we will delete what we are not legally required to retain.",
  "privacy.s6.title": "6. Cookies",
  "privacy.s6.body": "We use essential cookies to make our site work. We do not use advertising or tracking cookies. If this changes, we will update this policy and ask for your consent.",
  "privacy.s7.title": "7. Your rights",
  "privacy.s7.intro": "Under GDPR, you have the right to:",
  "privacy.s7.access": "Access the personal data we hold about you",
  "privacy.s7.correct": "Correct inaccurate data",
  "privacy.s7.delete": "Request deletion of your data",
  "privacy.s7.restrict": "Restrict or object to processing",
  "privacy.s7.portability": "Data portability",
  "privacy.s7.withdraw": "Withdraw consent at any time",
  "privacy.s7.complaint": "Lodge a complaint with the Swedish Authority for Privacy Protection (Integritetsskyddsmyndigheten, IMY) at imy.se",
  "privacy.s8.title": "8. Changes to this policy",
  "privacy.s8.body": "We may update this policy from time to time. Material changes will be communicated before they take effect.",
  "privacy.s9.title": "9. Contact",
  "privacy.s9.body": "Questions about your data or this policy:",
  // Terms page (guest-facing)
  "terms.heading": "Terms of Service",
  "terms.version": "Version 1.0 — Effective 1 March 2025",
  "terms.s1.title": "1. Who we are",
  "terms.s1.body": "Guestly Homes Sweden AB, org. nr 559359-3238, 944 91 Hortlax, Sweden. We operate the Guestly Experiences community at guestlyexperiences.com.",
  "terms.s2.title": "2. What we do",
  "terms.s2.body": "Guestly Experiences is a community where independent experience hosts (\"Hosts\") offer activities for you to book. We connect you with Hosts — we do not operate, supervise, or deliver the experiences ourselves. When you book, you enter into an agreement directly with the Host.",
  "terms.s3.title": "3. Bookings",
  "terms.s3.body": "When you complete a booking, you submit a binding request to the Host. The booking is confirmed once you receive a confirmation from us on behalf of the Host. Please review the confirmation to ensure all details are correct. Each experience listing states what is included, the meeting point, duration, and any requirements.",
  "terms.s4.title": "4. Pricing and payment",
  "terms.s4.body": "Prices shown include applicable taxes unless stated otherwise in the listing. Payment is collected at the time of booking via our payment provider. A payment hold may be placed on your card — the amount is held, not charged, until the booking is confirmed. By completing payment to Guestly, you fulfil your payment obligation towards the Host.",
  "terms.s5.title": "5. Cancellation",
  "terms.s5.body": "You may cancel free of charge up to the cancellation deadline stated on the listing. After the deadline, or in case of no-show, no refund is provided unless otherwise stated. If a Host cancels, you will receive a full refund. Refunds are returned to your original payment method.",
  "terms.s6.title": "6. Your responsibilities",
  "terms.s6.body": "You are responsible for arriving on time, meeting any participation requirements stated in the listing (such as fitness level, age, or equipment), and following the Host's safety instructions. You must hold valid travel documents if applicable. Guestly does not provide insurance — we recommend you arrange your own travel or activity insurance.",
  "terms.s7.title": "7. Host responsibilities",
  "terms.s7.body": "Hosts are independent operators, not employees or agents of Guestly. They are responsible for delivering the experience as described, maintaining appropriate insurance and certifications, and complying with applicable laws and safety regulations.",
  "terms.s8.title": "8. Reviews",
  "terms.s8.body": "After an experience, you may be invited to leave a review. Reviews reflect personal opinions and must be truthful. You may not post content that is misleading, defamatory, or that violates the rights of others. By posting a review, you grant Guestly a non-exclusive, royalty-free licence to use it on our platform and marketing channels.",
  "terms.s9.title": "9. Limitation of liability",
  "terms.s9.body": "Guestly acts as an intermediary. We are not liable for the acts, omissions, or negligence of any Host, or for any injury, loss, or damage arising from an experience. Our total liability to you shall not exceed the amount you paid for the relevant booking. Nothing in these terms excludes liability for fraud, gross negligence, or death or personal injury caused by our negligence.",
  "terms.s10.title": "10. Data protection",
  "terms.s10.body": "We process your personal data in accordance with the EU General Data Protection Regulation (GDPR) and Swedish law. For details, see our Privacy Policy.",
  "terms.s11.title": "11. Changes to these terms",
  "terms.s11.body": "We may update these terms from time to time. Material changes will be communicated to you before they take effect. Continued use of the community after changes constitutes acceptance.",
  "terms.s12.title": "12. Governing law",
  "terms.s12.body": "These terms are governed by Swedish law. Disputes shall be resolved by the Swedish courts. If you are a consumer within the EU, you may also bring proceedings in your country of residence.",
  "terms.s12.odr": "You may also use the EU Online Dispute Resolution platform.",
  "terms.s13.title": "13. Contact",
  "terms.s13.body": "Questions about these terms:",
  // NotFound page
  "notFound.heading": "404",
  "notFound.description": "This page doesn't exist",
  "notFound.link": "Back to experiences",
  // Booking management page
  "bookingManage.backToExperiences": "Back to experiences",
  "bookingManage.notFoundTitle": "Booking not found",
  "bookingManage.notFoundDesc": "This link may have expired or the booking no longer exists.",
  "bookingManage.browseExperiences": "Browse experiences",
  "bookingManage.yourBooking": "Your booking",
  "bookingManage.ref": "Ref:",
  "bookingManage.cardHeld": "Your card is held for {amount} SEK. You'll only be charged if the host confirms.",
  "bookingManage.totalPrice": "Total price",
  "bookingManage.yourDetails": "Your details",
  "bookingManage.name": "Name:",
  "bookingManage.email": "Email:",
  "bookingManage.notes": "Notes:",
  "bookingManage.newTimeSuggestion": "The host suggested a new time",
  "bookingManage.accept": "Accept",
  "bookingManage.decline": "Decline",
  "bookingManage.cancellation": "Cancellation",
  "bookingManage.cancelFreeDesc": "You can cancel freely — your card hold will be released in full.",
  "bookingManage.cancelLateDesc": "This is a late cancellation. A {percent}% refund of {amount} SEK applies.",
  "bookingManage.cancelFreeHours": "Cancel up to {hours} hours before start, no charge.",
  "bookingManage.cancelLateRefund": "{percent}% refund for later cancellations.",
  "bookingManage.cancelButton": "Cancel booking",
  "bookingManage.cancelDialogTitle": "Cancel this booking?",
  "bookingManage.cancelDialogFree": "Your card hold will be released. No charge will be made.",
  "bookingManage.cancelDialogLate": "This is a late cancellation. You will receive a {percent}% refund ({amount} SEK).",
  "bookingManage.keepBooking": "Keep my booking",
  "bookingManage.yesCancel": "Yes, cancel",
  "bookingManage.bookingCancelled": "Booking cancelled",
  "bookingManage.holdReleased": "Your card hold has been released.",
  "bookingManage.refundProcessed": "A {percent}% refund of {amount} SEK will be processed.",
  "bookingManage.submittedOn": "Submitted on {date}",
  "bookingManage.guest": "guest",
  "bookingManage.guests": "guests",
  "bookingManage.status.pending": "Pending",
  "bookingManage.status.pendingDesc": "Your request is with the host. Your card is held but not charged.",
  "bookingManage.status.approved": "Approved",
  "bookingManage.status.approvedDesc": "You're all set. Payment has been processed.",
  "bookingManage.status.declined": "Declined",
  "bookingManage.status.declinedDesc": "This time didn't work out. Your card hold has been released.",
  "bookingManage.status.expired": "Expired",
  "bookingManage.status.expiredDesc": "This request has expired",
  "bookingManage.status.cancelled": "Cancelled",
  "bookingManage.status.cancelledDesc": "This booking was cancelled",
  // Review page
  "review.shareFeedback": "Share your feedback",
  "review.takesUnderMinute": "Takes under a minute.",
  "review.npsQuestion": "How likely are you to recommend this experience to a friend or colleague?",
  "review.npsLow": "0 = Not at all likely · 10 = Extremely likely",
  "review.continue": "Continue",
  "review.back": "Back",
  "review.thankYouRating": "Thank you for rating us {score}/10",
  "review.whatMadeYouChoose": "What made you choose that score?",
  "review.helperHigh": "What went well?",
  "review.helperMid": "What would make it a 9 or 10?",
  "review.helperLow": "What could we do better?",
  "review.quickNote": "A quick note (optional)",
  "review.quickRatings": "Quick ratings",
  "review.ratingsScale": "1 = Poor · 5 = Excellent",
  "review.organisation": "Organisation",
  "review.hostQuality": "Host quality",
  "review.valueForMoney": "Value for money",
  "review.safety": "Safety",
  "review.sendFeedback": "Send feedback",
  "review.sharePhotos": "Share your photos",
  "review.helpFutureGuests": "Help future guests see what to expect.",
  "review.maxPhotos": "Maximum 5 photos reached",
  "review.tapToSelect": "Tap to select or drag photos here",
  "review.fileFormats": "JPG, PNG or WebP · Max 5MB each",
  "review.uploading": "Uploading...",
  "review.skip": "Skip",
  "review.thankYou": "Thank you.",
  "review.feedbackHelps": "Your feedback helps us improve.",
  "review.redirecting": "Redirecting in {seconds}s...",
  "review.exploreExperiences": "Explore experiences",
  "review.notFound": "Review not found or link has expired.",
  "review.somethingWrong": "Something went wrong.",
  // Checkout
  "checkout.reviewAndPay": "Review and pay",
  "checkout.goBack": "Go back",
  "checkout.oneMoment": "One moment...",
  "checkout.confirmAndPay": "Confirm and pay {total} SEK",
  // Booking confirmation
  "confirmation.requestReceived": "Request received",
  "confirmation.body": "We've sent your request. The host will review it and get back to you soon.",
  "confirmation.total": "Total:",
  "confirmation.reference": "Reference:",
  "confirmation.trackBooking": "Track your booking",
  // Reviews summary (public detail page)
  "reviews.readAll": "Read all {count} reviews",
  "reviews.howItWorks": "About guest reviews",
  "reviews.howItWorksLink": "How our reviews work",
  "reviews.searchPlaceholder": "Search reviews",
  "reviews.sortLatest": "Latest",
  "reviews.sortHighest": "Highest",
  "reviews.sortLowest": "Lowest",
  "reviews.emptySearch": "Nothing found. Try a different search.",
  "reviews.showMore": "Show more",
  "reviews.showLess": "Show less",
  "reviews.infoP1": "Each review comes from a guest who completed their experience. We collect feedback within 48 hours, while the memory is still fresh.",
  "reviews.infoP2": "Guests rate five areas — emotional experience, organisation, host, value, and safety — and share what stood out. Every review is verified and unedited.",
  "reviews.infoP3": "If something doesn't meet our guidelines, we step in. Honest feedback keeps standards high for everyone.",
  "reviews.helpCentre": "Visit our help centre",
  "reviews.aiTranslated": "AI-translated, view in original language",
  "reviews.viewTranslated": "Translated, view in {language}",
  // Micro snippets
  "snippet.reviews": "{count} reviews",
  "snippet.hostedBy": "Hosted by {name}",
  "snippet.showAllPhotos": "Show all photos",
  "snippet.usuallyBookedOut": "Usually booked out",
  "snippet.guestsBookedThisWeek": "{count} guests booked this week",
  "snippet.overview": "Overview",
  "snippet.itinerary": "Itinerary",
  "snippet.reviews.nav": "Reviews",
  "snippet.host": "Host",
  "snippet.location": "Location",
  // Returning visitor hero
  "hero.returningHeadline": "Pick up where you left off",
  // Country & currency labels
  "partner.modal.country.SE": "Sweden",
  "partner.modal.country.NO": "Norway",
  "partner.modal.country.DK": "Denmark",
  "partner.modal.country.FI": "Finland",
  "partner.register.currency.SEK": "SEK — Swedish Krona",
  "partner.register.currency.EUR": "EUR — Euro",
  "partner.register.currency.NOK": "NOK — Norwegian Krone",
  "partner.register.currency.DKK": "DKK — Danish Krone",
  // WhyPartner section
  "partner.why.headline": "How we make it easier to run your experience business",
  "partner.why.subline": "More of the right guests. Less admin. Full control.",
  "partner.why.0.title": "Fill your quiet periods",
  "partner.why.0.body": "We connect you with guests already staying in our homes. People who are on the ground and looking for something to do. You get bookings when it's otherwise quiet.",
  "partner.why.1.title": "You keep control",
  "partner.why.1.body": "Your brand. Your prices. Your rules. We distribute your experience — you decide how it's delivered. No template that changes your business.",
  "partner.why.2.title": "We handle booking and payment",
  "partner.why.2.body": "Automated booking flow. Clear payment handling. No chasing invoices. You focus on the experience.",
  "partner.why.3.title": "The right guests — not mass market",
  "partner.why.3.body": "Our guests are business travellers and quality-conscious visitors. They book in advance. They show up. They appreciate quality.",
  "partner.why.4.title": "You only pay when you earn",
  "partner.why.4.body": "No startup fee. No monthly fee. Clear commission per completed booking. Simple and transparent.",
  "partner.why.5.title": "A local partner — not a global marketplace",
  "partner.why.5.body": "We build relationships in every city. We highlight local entrepreneurs. You're not an anonymous listing in a feed.",
  "partner.why.faq.0.q": "How does the commission work?",
  "partner.why.faq.0.a": "You only pay a percentage per completed booking. No cost if no booking happens.",
  "partner.why.faq.1.q": "When do I get paid?",
  "partner.why.faq.1.a": "Payouts happen automatically on the agreed cycle. Full visibility in your partner dashboard.",
  "partner.why.faq.2.q": "Do I have to leave other platforms?",
  "partner.why.faq.2.a": "No. You can continue selling through your own channels and other platforms.",
  "partner.why.faq.3.q": "Can I pause whenever I want?",
  "partner.why.faq.3.a": "Yes. You control your own availability and calendar.",
  "partner.why.cta.headline": "Ready to reach more of the right guests?",
  "partner.why.cta.subline": "We'll have a short call and see if it's a fit.",
  "partner.why.cta.primary": "Book an intro meeting",
  "partner.why.cta.secondary": "Create partner account",
  "partner.why.cta.note": "No lock-in during the startup phase.",
  // ToolsSection
  "partner.tools2.headline": "You don't need more tools. You need more of the right bookings.",
  "partner.tools2.subline": "We build the system around your business — not the other way around.",
  "partner.tools2.0.title": "Full overview",
  "partner.tools2.0.body": "See bookings, revenue, and upcoming activities in real time. No spreadsheets. No manual puzzling.",
  "partner.tools2.1.title": "Smarter presentation",
  "partner.tools2.1.body": "We help you structure descriptions that convert. You bring the content. We bring the structure.",
  "partner.tools2.2.title": "Demand insights",
  "partner.tools2.2.body": "See when guests are searching for similar experiences. Adjust times and availability based on actual demand.",
  // StatsStrip
  "partner.stats2.headline": "Local entrepreneurs in cities across Sweden",
  "partner.stats2.0.value": "100,000+",
  "partner.stats2.0.label": "Guest nights in our homes every year",
  "partner.stats2.1.value": "6",
  "partner.stats2.1.label": "Cities where we work with local hosts (new cities every month)",
  "partner.stats2.2.value": "20+",
  "partner.stats2.2.label": "Different types of experiences",
  "partner.stats2.3.value": "10",
  "partner.stats2.3.label": "Languages our guests book in",
  "partner.stats2.footer": "We match local experiences with guests who are already on the ground.",
  // Requirements
  "partner.req.headline": "What you need to have in place",
  "partner.req.subline": "We work with serious local operators.",
  "partner.req.0": "Registered company or sole proprietorship",
  "partner.req.1": "Tax registration (F-skatt in Sweden)",
  "partner.req.2": "Business liability insurance",
  "partner.req.3": "Clear safety routines",
  "partner.req.4": "Proper documentation for activities requiring permits",
  "partner.req.5": "High-quality images",
  "partner.req.6": "Clear instructions for guests",
  "partner.req.unsure.title": "Not sure if you meet the requirements?",
  "partner.req.unsure.body": "Most serious operators do. Book a short call and we'll go through it together.",
  "partner.req.unsure.cta": "Talk to us",
  // PartnerFAQ
  "partner.faq2.headline": "Common questions from local entrepreneurs",
  "partner.faq2.0.q": "What does it cost to join?",
  "partner.faq2.0.a": "No startup fee. No monthly fee. Commission is only taken on completed bookings.",
  "partner.faq2.1.q": "When do I get paid?",
  "partner.faq2.1.a": "Payouts happen automatically on the agreed cycle. Full transparency in your partner dashboard.",
  "partner.faq2.2.q": "Can I use my own booking system?",
  "partner.faq2.2.a": "Yes. You can continue using your own channels in parallel.",
  "partner.faq2.3.q": "Do I have to be exclusive?",
  "partner.faq2.3.a": "No. We don't require exclusivity.",
  "partner.faq2.4.q": "How quickly can I get my first booking?",
  "partner.faq2.4.a": "It depends on city, season, and category. Many get their first booking shortly after publishing.",
  // PartnerFinalCTA
  "partner.final.headline": "Want to reach more of the right guests?",
  "partner.final.subline": "We'll have a short call and see if the partnership is a fit.",
  "partner.final.primary": "Book an intro meeting",
  "partner.final.secondary": "Create partner account",
  "partner.final.note": "No lock-in during the startup phase.",
  // Guest account pages
  "account.heading": "My Account",
  "account.subheading": "Manage your orders, favorites, and profile.",
  "account.tab.orders": "Orders",
  "account.tab.favorites": "Favorites",
  "account.tab.messages": "Messages",
  "account.tab.profile": "Profile",
  "account.orders.empty": "No orders yet.",
  "account.orders.browse": "Browse experiences",
  "account.orders.pending": "Pending",
  "account.orders.paid": "Paid",
  "account.orders.confirmed": "Confirmed",
  "account.orders.cancelled": "Cancelled",
  "account.orders.view": "View",
  "account.favorites.empty": "No favorites yet.",
  "account.favorites.browse": "Browse experiences",
  "account.messages.empty": "No messages yet.",
  "account.messages.goToHelp": "Go to Help Center",
  "account.messages.sent": "Sent",
  "account.messages.read": "Read",
  "account.messages.replied": "Replied",
  "account.messages.defaultSubject": "Message",
  "account.profile.email": "Email",
  "account.profile.emailNote": "Email cannot be changed.",
  "account.profile.name": "Name",
  "account.profile.namePlaceholder": "Your name",
  "account.profile.phone": "Phone",
  "account.profile.phonePlaceholder": "+46 70 123 4567",
  "account.profile.save": "Save changes",
  "account.profile.saved": "Profile updated",
  "account.profile.error": "Failed to update profile",
  // Affiliate portal
  "affiliate.nav.signIn": "Sign In",
  "affiliate.nav.applyNow": "Apply Now",
  "affiliate.landing.badge": "Affiliate Program",
  "affiliate.landing.heroTitle": "Grow with Guestly.",
  "affiliate.landing.heroHighlight": "Earn as you go.",
  "affiliate.landing.heroDesc": "Two programs, one platform. Recruit providers or promote experiences — earn commissions either way.",
  "affiliate.landing.learnMore": "Learn More",
  "affiliate.landing.chooseProgram": "Choose Your Program",
  "affiliate.landing.chooseProgramDesc": "Pick the program that fits your strengths — or apply as a dual partner.",
  "affiliate.landing.recruiterTitle": "Provider Recruiter",
  "affiliate.landing.recruiterSubtitle": "Recruit experience providers",
  "affiliate.landing.recruiterDesc": "Earn a one-time bonus plus recurring commissions from Guestly's platform fee for up to 36 months.",
  "affiliate.landing.recruiterH0": "One-time activation bonus",
  "affiliate.landing.recruiterH1": "Recurring % of platform fee",
  "affiliate.landing.recruiterH2": "36-month commission window",
  "affiliate.landing.recruiterBadge": "Most Popular",
  "affiliate.landing.creatorTitle": "Content Creator",
  "affiliate.landing.creatorSubtitle": "Promote experiences to travelers",
  "affiliate.landing.creatorDesc": "Share unique experiences with your audience. Earn commission on every booking made through your content links.",
  "affiliate.landing.creatorH0": "8% per booking commission",
  "affiliate.landing.creatorH1": "30-day attribution window",
  "affiliate.landing.creatorH2": "Track per-content performance",
  "affiliate.landing.creatorBadge": "New",
  "affiliate.landing.howItWorks": "How It Works",
  "affiliate.landing.step0Title": "Apply",
  "affiliate.landing.step0Desc": "Pick your program and register in 2 minutes.",
  "affiliate.landing.step1Title": "Share & Promote",
  "affiliate.landing.step1Desc": "Use your referral links in content, emails, or social media.",
  "affiliate.landing.step2Title": "Earn Commissions",
  "affiliate.landing.step2Desc": "Get paid monthly via Stripe Connect or bank transfer.",
  "affiliate.landing.tiersTitle": "Provider Recruiter Tiers",
  "affiliate.landing.tiersDesc": "The more providers you activate, the higher your rewards.",
  "affiliate.landing.activatedProviders": "activated providers",
  "affiliate.landing.oneTimeBonus": "one-time bonus",
  "affiliate.landing.ofPlatformFee": "of platform fee",
  "affiliate.landing.creatorCommissions": "Content Creator Commissions",
  "affiliate.landing.perBooking": "Per-booking commission rate",
  "affiliate.landing.attributionWindow": "Attribution window",
  "affiliate.landing.trackContent": "Track each piece of content",
  "affiliate.landing.trustTitle": "Why Partners Trust Guestly",
  "affiliate.landing.trustTransparent": "Transparent Payouts",
  "affiliate.landing.trustTransparentDesc": "Every commission is inspectable. No hidden logic.",
  "affiliate.landing.trustAnalytics": "Real-Time Analytics",
  "affiliate.landing.trustAnalyticsDesc": "Track clicks, bookings, and earnings in real time.",
  "affiliate.landing.trustMonthly": "Monthly Payouts",
  "affiliate.landing.trustMonthlyDesc": "Stripe Connect or bank transfer. SEK & EUR.",
  "affiliate.landing.ctaTitle": "Ready to start earning?",
  "affiliate.landing.ctaDesc": "Join our affiliate program today. It takes less than 2 minutes to apply.",
  "affiliate.landing.terms": "Terms",
  "affiliate.landing.privacy": "Privacy",
  "affiliate.landing.faq": "FAQ",
  "affiliate.apply.title": "Become an Affiliate",
  "affiliate.apply.desc": "Choose your program and start earning commissions.",
  "affiliate.apply.program": "Program",
  "affiliate.apply.providerRecruiter": "Provider Recruiter",
  "affiliate.apply.providerRecruiterDesc": "Recruit experience providers to Guestly",
  "affiliate.apply.contentCreator": "Content Creator",
  "affiliate.apply.contentCreatorDesc": "Promote experiences to your audience",
  "affiliate.apply.dualPartner": "Dual Partner",
  "affiliate.apply.dualPartnerDesc": "Both recruit providers and create content",
  "affiliate.apply.yourName": "Your Name",
  "affiliate.apply.companyBrand": "Company / Brand",
  "affiliate.apply.email": "Email",
  "affiliate.apply.password": "Password",
  "affiliate.apply.country": "Country",
  "affiliate.apply.phone": "Phone",
  "affiliate.apply.website": "Website / Social Profile",
  "affiliate.apply.submit": "Apply Now",
  "affiliate.apply.submitting": "Submitting…",
  "affiliate.apply.termsAgree": "By applying you agree to our",
  "affiliate.apply.termsLink": "Terms",
  "affiliate.apply.privacyLink": "Privacy Policy",
  "affiliate.apply.and": "and",
  "affiliate.apply.sweden": "Sweden",
  "affiliate.apply.finland": "Finland",
  "affiliate.apply.norway": "Norway",
  "affiliate.apply.denmark": "Denmark",
  "affiliate.login.title": "Affiliate Portal",
  "affiliate.login.desc": "Sign in to your affiliate account",
  "affiliate.login.email": "Email",
  "affiliate.login.password": "Password",
  "affiliate.login.signIn": "Sign In",
  "affiliate.login.signingIn": "Signing in…",
  "affiliate.login.sendMagicLink": "Send Magic Link",
  "affiliate.login.useMagicLink": "Use magic link instead",
  "affiliate.login.usePassword": "Use password instead",
  "affiliate.login.noAccount": "Don't have an account?",
  "affiliate.login.applyNow": "Apply now",
  "affiliate.faq.title": "Frequently Asked Questions",
  "affiliate.faq.0.q": "How does the Provider Recruiter Program work?",
  "affiliate.faq.0.a": "You recruit experience providers to join Guestly Experiences. When a provider you referred becomes eligible (active 90 days + 10 bookings or 30,000 SEK GMV), you earn a one-time bonus plus recurring commission from Guestly's platform fee for 36 months.",
  "affiliate.faq.1.q": "What is the commission based on?",
  "affiliate.faq.1.a": "Your commission is always a percentage of Guestly's platform fee — never a percentage of total booking value (GMV).",
  "affiliate.faq.2.q": "How are the tiers structured?",
  "affiliate.faq.2.a": "Bronze (default): 1,000 SEK bonus + 3%. Silver (5+): 1,500 SEK + 5%. Gold (15+): 2,500 SEK + 7%.",
  "affiliate.faq.3.q": "When do I get paid?",
  "affiliate.faq.3.a": "Payouts are processed monthly on the 4th working day. Minimum payout threshold is 2,000 SEK.",
  "affiliate.faq.4.q": "What counts as an 'activated' provider?",
  "affiliate.faq.4.a": "Active on the platform for 90 days AND 10 completed bookings or 30,000 SEK in GMV.",
  "affiliate.faq.5.q": "Who gets credit for a referral?",
  "affiliate.faq.5.a": "First referral wins, always.",
  "affiliate.faq.6.q": "Can I be both a Provider Recruiter and a Creator Affiliate?",
  "affiliate.faq.6.a": "Dual participation is possible but requires admin approval.",
  "affiliate.faq.7.q": "What currencies are supported?",
  "affiliate.faq.7.a": "SEK and EUR at launch.",
  "affiliate.creators.badge": "Content Creator Program",
  "affiliate.creators.heroTitle": "Share experiences.",
  "affiliate.creators.heroHighlight": "Earn from every booking.",
  "affiliate.creators.heroDesc": "Turn your content into revenue. Promote unique experiences to your audience and earn 8% commission on every booking through your links.",
  "affiliate.creators.applyAsCreator": "Apply as Creator",
  "affiliate.creators.commissionPerBooking": "Commission per booking",
  "affiliate.creators.attributionWindow": "Attribution window",
  "affiliate.creators.minimumPayout": "Minimum payout",
  "affiliate.creators.howItWorks": "How It Works",
  "affiliate.creators.createContent": "Create Content",
  "affiliate.creators.createContentDesc": "Share experiences with your audience on Instagram, TikTok, YouTube, your blog, or any platform.",
  "affiliate.creators.useTrackingLinks": "Use Tracking Links",
  "affiliate.creators.useTrackingLinksDesc": "Generate unique tracking links for each piece of content to track performance individually.",
  "affiliate.creators.trackAndEarn": "Track & Earn",
  "affiliate.creators.trackAndEarnDesc": "Monitor clicks, bookings, and commissions in real-time from your dashboard.",
  "affiliate.creators.supportedPlatforms": "Supported Platforms",
  "affiliate.creators.platformDesc": "Create unique tracking links for each platform and piece of content.",
  "affiliate.creators.dashboardFeatures": "Creator Dashboard Features",
  "affiliate.creators.perContentAnalytics": "Per-Content Analytics",
  "affiliate.creators.perContentAnalyticsDesc": "See clicks, bookings, and revenue for each piece of content you create.",
  "affiliate.creators.multiPlatformTracking": "Multi-Platform Tracking",
  "affiliate.creators.multiPlatformTrackingDesc": "Generate different links for Instagram, YouTube, blog posts, and more.",
  "affiliate.creators.performanceTrends": "Performance Trends",
  "affiliate.creators.performanceTrendsDesc": "Daily performance charts show what content drives the most bookings.",
  "affiliate.creators.realTimeEarnings": "Real-Time Earnings",
  "affiliate.creators.realTimeEarningsDesc": "Watch your commission balance grow in real-time as bookings come in.",
  "affiliate.creators.exampleEarnings": "Example Earnings",
  "affiliate.creators.readyToMonetize": "Ready to monetize your content?",
  "affiliate.creators.readyToMonetizeDesc": "Apply now and start earning from your very first post.",
  "affiliate.recruiters.badge": "Provider Recruiter Program",
  "affiliate.recruiters.heroTitle": "Recruit providers.",
  "affiliate.recruiters.heroHighlight": "Earn for 36 months.",
  "affiliate.recruiters.heroDesc": "Know someone who runs experiences? Refer them to Guestly and earn a one-time bonus plus recurring commissions from every booking — for 3 full years.",
  "affiliate.recruiters.eligibilityTitle": "How Providers Reach Eligibility",
  "affiliate.recruiters.milestone0": "Provider Applies",
  "affiliate.recruiters.milestone1": "Provider Approved",
  "affiliate.recruiters.milestone2": "First Experience Published",
  "affiliate.recruiters.milestone3": "First Booking",
  "affiliate.recruiters.milestone4": "90 Days Active + 10 Bookings / 30k SEK",
  "affiliate.recruiters.milestone5": "You Get Paid!",
  "affiliate.recruiters.exampleEarnings": "Example Earnings",
  "affiliate.recruiters.readyToRecruit": "Ready to start recruiting?",
  "affiliate.sidebar.dashboard": "Dashboard",
  "affiliate.sidebar.invite": "Invite Providers",
  "affiliate.sidebar.providers": "Referred Providers",
  "affiliate.sidebar.contentLinks": "Content Links",
  "affiliate.sidebar.contentPerformance": "Content Performance",
  "affiliate.sidebar.earnings": "Earnings",
  "affiliate.sidebar.analytics": "Analytics",
  "affiliate.sidebar.aiCopilot": "AI Copilot",
  "affiliate.sidebar.invoices": "Invoices",
  "affiliate.sidebar.notifications": "Notifications",
  "affiliate.sidebar.settings": "Settings",
  "affiliate.sidebar.signOut": "Sign Out",
  "affiliate.sidebar.portalTitle": "Affiliate Portal",
  "affiliate.sidebar.more": "More",
  "affiliate.dashboard.welcomeBack": "Welcome back,",
  "affiliate.dashboard.recruiterDashboard": "Provider Recruiter Dashboard",
  "affiliate.dashboard.totalReferred": "Total Referred",
  "affiliate.dashboard.providersReferred": "Providers referred",
  "affiliate.dashboard.pending": "Pending",
  "affiliate.dashboard.awaitingActivation": "Awaiting activation",
  "affiliate.dashboard.activated": "Activated",
  "affiliate.dashboard.eligibleProviders": "Eligible providers",
  "affiliate.dashboard.pendingEarnings": "Pending Earnings",
  "affiliate.dashboard.approvedEarnings": "Approved Earnings",
  "affiliate.dashboard.totalPaid": "Total Paid",
  "affiliate.dashboard.tierProgress": "Tier Progress",
  "affiliate.dashboard.activatedProviders": "activated provider",
  "affiliate.dashboard.activatedProvidersPlural": "activated providers",
  "affiliate.dashboard.moreToReach": "more to reach",
  "affiliate.dashboard.maxTier": "Maximum tier reached!",
  "affiliate.dashboard.referralCode": "Your Referral Code",
  "affiliate.dashboard.copyLink": "Copy link",
  "affiliate.dashboard.shareCode": "Share this code with providers you want to recruit to Guestly Experiences.",
  "affiliate.dashboard.noAccount": "No affiliate account found. Please complete onboarding.",
  "affiliate.earnings.title": "Earnings",
  "affiliate.earnings.desc": "Track your commissions and payouts.",
  "affiliate.earnings.pending": "Pending",
  "affiliate.earnings.locked": "Locked",
  "affiliate.earnings.approved": "Approved",
  "affiliate.earnings.totalPaid": "Total Paid",
  "affiliate.earnings.ledger": "Commission Ledger",
  "affiliate.earnings.noCommissions": "No commissions yet. Commissions are created when your referred providers reach eligibility milestones.",
  "affiliate.earnings.type": "Type",
  "affiliate.earnings.amount": "Amount",
  "affiliate.earnings.status": "Status",
  "affiliate.earnings.date": "Date",
  "affiliate.invite.title": "Invite Providers",
  "affiliate.invite.desc": "Share your referral link or send personal invitations.",
  "affiliate.invite.newInvite": "New Invite",
  "affiliate.invite.sendInvite": "Send Provider Invite",
  "affiliate.invite.providerName": "Provider Name",
  "affiliate.invite.email": "Email",
  "affiliate.invite.campaignTag": "Campaign Tag",
  "affiliate.invite.optional": "optional",
  "affiliate.invite.send": "Send Invite",
  "affiliate.invite.sending": "Sending…",
  "affiliate.invite.referralLink": "Your Referral Link",
  "affiliate.invite.campaigns": "Campaigns:",
  "affiliate.invite.inviteHistory": "Invite History",
  "affiliate.invite.all": "All",
  "affiliate.invite.noInvites": "No invites sent yet.",
  "affiliate.invite.noFilteredInvites": "No {status} invites.",
  "affiliate.invite.name": "Name",
  "affiliate.invite.campaign": "Campaign",
  "affiliate.invite.status": "Status",
  "affiliate.invite.date": "Date",
  "affiliate.invite.resend": "Resend",
  "affiliate.invite.expire": "Expire",
  "affiliate.providers.title": "Referred Providers",
  "affiliate.providers.desc": "Track the progress of providers you've referred.",
  "affiliate.providers.empty": "No referred providers yet. Share your referral link to get started.",
  "affiliate.providers.provider": "Provider",
  "affiliate.providers.status": "Status",
  "affiliate.providers.approved": "Approved",
  "affiliate.providers.firstBooking": "First Booking",
  "affiliate.providers.eligible": "Eligible",
  "affiliate.providers.created": "Created",
  "affiliate.analytics.title": "Analytics",
  "affiliate.analytics.desc": "Performance insights for your referral program.",
  "affiliate.analytics.last7": "Last 7 days",
  "affiliate.analytics.last30": "Last 30 days",
  "affiliate.analytics.last90": "Last 90 days",
  "affiliate.analytics.linkClicks": "Link Clicks",
  "affiliate.analytics.invitesSent": "Invites Sent",
  "affiliate.analytics.allTime": "All time",
  "affiliate.analytics.providersReferred": "Providers Referred",
  "affiliate.analytics.activated": "Activated",
  "affiliate.analytics.conversionRate": "Conversion Rate",
  "affiliate.analytics.referredToActivated": "Referred → Activated",
  "affiliate.analytics.avgActivationTime": "Avg Activation Time",
  "affiliate.analytics.totalCommissions": "Total Commissions",
  "affiliate.analytics.pending": "Pending",
  "affiliate.analytics.clicksOverTime": "Referral Link Clicks Over Time",
  "affiliate.analytics.inviteConversionFunnel": "Invite Conversion Funnel",
  "affiliate.analytics.invitesSentLabel": "Invites Sent",
  "affiliate.analytics.clicked": "Clicked",
  "affiliate.analytics.applied": "Applied",
  "affiliate.analytics.activatedLabel": "Activated",
  "affiliate.analytics.referralsByStatus": "Referrals by Status",
  "affiliate.analytics.milestoneAchievements": "Milestone Achievements",
  "affiliate.analytics.noData": "No analytics data available.",
  "affiliate.settings.title": "Settings",
  "affiliate.settings.desc": "Manage your affiliate profile.",
  "affiliate.settings.profile": "Profile",
  "affiliate.settings.displayName": "Display Name",
  "affiliate.settings.companyName": "Company Name",
  "affiliate.settings.phone": "Phone",
  "affiliate.settings.website": "Website",
  "affiliate.settings.country": "Country",
  "affiliate.settings.currency": "Currency",
  "affiliate.settings.save": "Save Changes",
  "affiliate.settings.saving": "Saving…",
  "affiliate.settings.accountInfo": "Account Info",
  "affiliate.settings.email": "Email",
  "affiliate.settings.referralCode": "Referral Code",
  "affiliate.settings.status": "Status",
  "affiliate.settings.memberSince": "Member Since",
  "affiliate.notifications.title": "Notifications",
  "affiliate.notifications.desc": "Stay updated on your affiliate activity.",
  "affiliate.notifications.empty": "No notifications yet.",
  "affiliate.invoices.title": "Invoices",
  "affiliate.invoices.desc": "Your self-billing invoices and credit notes",
  "affiliate.invoices.loading": "Loading…",
  "affiliate.invoices.empty": "No invoices yet. Invoices are generated when payouts are processed.",
  "affiliate.invoices.invoiceNumber": "Invoice #",
  "affiliate.invoices.type": "Type",
  "affiliate.invoices.creditNote": "Credit Note",
  "affiliate.invoices.invoice": "Invoice",
  "affiliate.invoices.amount": "Amount",
  "affiliate.invoices.status": "Status",
  "affiliate.invoices.issued": "Issued",
  "affiliate.invoices.due": "Due",
  "affiliate.contentLinks.title": "Content Links",
  "affiliate.contentLinks.desc": "Create tracking links for your content and monitor performance.",
  "affiliate.contentLinks.newLink": "New Link",
  "affiliate.contentLinks.createLink": "Create Content Link",
  "affiliate.contentLinks.platform": "Platform",
  "affiliate.contentLinks.contentTitle": "Content Title",
  "affiliate.contentLinks.contentUrl": "Content URL",
  "affiliate.contentLinks.linkToExperience": "Link to Experience",
  "affiliate.contentLinks.allExperiences": "All Experiences",
  "affiliate.contentLinks.campaignTag": "Campaign Tag",
  "affiliate.contentLinks.create": "Create Link",
  "affiliate.contentLinks.creating": "Creating…",
  "affiliate.contentLinks.empty": "No content links yet. Create your first tracking link to start earning.",
  "affiliate.contentLinks.content": "Content",
  "affiliate.contentLinks.clicks": "Clicks",
  "affiliate.contentLinks.bookings": "Bookings",
  "affiliate.contentLinks.revenue": "Revenue",
  "affiliate.contentLinks.status": "Status",
  "affiliate.contentLinks.actions": "Actions",
  "affiliate.contentPerformance.title": "Content Performance",
  "affiliate.contentPerformance.desc": "Track how your content drives bookings.",
  "affiliate.contentPerformance.totalClicks": "Total Clicks",
  "affiliate.contentPerformance.bookings": "Bookings",
  "affiliate.contentPerformance.revenueGenerated": "Revenue Generated",
  "affiliate.contentPerformance.commissionEarned": "Commission Earned",
  "affiliate.contentPerformance.clicksAndBookings": "Clicks & Bookings Over Time",
  "affiliate.contentPerformance.performanceByPlatform": "Performance by Platform",
  "affiliate.contentPerformance.topPerforming": "Top Performing Content",
  "affiliate.aiCopilot.title": "AI Performance Copilot",
  "affiliate.aiCopilot.desc": "Get personalized tips to boost your affiliate performance",
  "affiliate.aiCopilot.getTips": "Get Tips",
  "affiliate.aiCopilot.analyzing": "Analyzing…",
  "affiliate.aiCopilot.readyTitle": "Ready to optimize?",
  "affiliate.aiCopilot.readyDesc": "Click \"Get Tips\" to receive AI-powered performance suggestions based on your data.",
  "affiliate.aiCopilot.analyzingData": "Analyzing your performance data…",
  "affiliate.onboarding.title": "Complete Your Profile",
  "affiliate.onboarding.stepOf": "Step {current} of {total}:",
  "affiliate.onboarding.businessType": "Business Type",
  "affiliate.onboarding.legalInfo": "Legal Information",
  "affiliate.onboarding.contactDetails": "Contact Details",
  "affiliate.onboarding.payoutSetup": "Payout Setup",
  "affiliate.onboarding.termsAndConditions": "Terms & Conditions",
  "affiliate.onboarding.individualOrCompany": "Are you registering as an individual or company?",
  "affiliate.onboarding.individual": "Individual / Sole Proprietor",
  "affiliate.onboarding.company": "Company / Organization",
  "affiliate.onboarding.country": "Country",
  "affiliate.onboarding.legalName": "Legal Name *",
  "affiliate.onboarding.companyName": "Company Name",
  "affiliate.onboarding.orgNumber": "Organization Number",
  "affiliate.onboarding.vatNumber": "VAT Number",
  "affiliate.onboarding.phone": "Phone",
  "affiliate.onboarding.websiteSocial": "Website / Social Link",
  "affiliate.onboarding.payoutMethod": "Preferred Payout Method",
  "affiliate.onboarding.stripeConnect": "Stripe Connect (recommended)",
  "affiliate.onboarding.bankTransfer": "Bank Transfer",
  "affiliate.onboarding.connectStripe": "Connect Stripe Account",
  "affiliate.onboarding.accountHolder": "Account Holder",
  "affiliate.onboarding.bankName": "Bank Name",
  "affiliate.onboarding.iban": "IBAN",
  "affiliate.onboarding.bicSwift": "BIC / SWIFT",
  "affiliate.onboarding.agreeTerms": "I agree to the",
  "affiliate.onboarding.affiliateTerms": "Affiliate Terms of Service",
  "affiliate.onboarding.agreePrivacy": "I agree to the",
  "affiliate.onboarding.privacyPolicy": "Privacy Policy",
  "affiliate.onboarding.back": "Back",
  "affiliate.onboarding.continue": "Continue",
  "affiliate.onboarding.complete": "Complete",
  "affiliate.onboarding.saving": "Saving…",
  // PublicLayout nav
  "nav.signIn": "Sign in",
  "nav.myAccount": "My Account",
  "nav.favorites": "Favorites",
  "nav.signOut": "Sign out",
  // ExperienceInfo
  "detail.perPerson": "/ person",
  "detail.perGroup": "/ group",
  "detail.priceOnRequest": "Price on request",
  "detail.guests": "{min}–{max} guests",
  "detail.linkCopied": "Link copied",
  "detail.cancelUpTo": "Cancel up to {hours} hours before start, no charge.",
  "detail.lateRefund": "{percent}% refund for later cancellations.",
  // GoodToKnow
  "detail.guestsPerSession": "{min}–{max} guests per session.",
  "detail.cancelPolicy": "Cancel up to {hours} hours before start, no charge.",
  "detail.lateRefundPolicy": "{percent}% refund after that.",
  "detail.responsibleForExperience": "They are responsible for this experience.",
  "detail.yourHostForExperience": "Your host for this experience.",
  // HostSection
  "detail.aboutHost": "About {name}",
  "detail.aboutYourHost": "About your host",
  // JourneySteps
  "detail.whatYoullDoHeading": "What you'll do",
  "detail.itineraryDisclaimer": "For reference only. Itineraries are subject to change.",
  "detail.experienceLanguage": "This experience is held in {language}.",
  "detail.hours": "{count} hour|{count} hours",
  "detail.minutes": "{count} min",
  // MobileBookingBar
  "mobile.checkDates": "Check dates",
  "mobile.bookExperience": "Book this experience",
  // ReviewsSummary
  "reviews.review": "{count} review|{count} reviews",
  "reviews.ratingOrganisation": "Organisation",
  "reviews.ratingHost": "Host",
  "reviews.ratingValue": "Value",
  "reviews.ratingSafety": "Safety",
  "reviews.guestPhoto": "Guest photo",
  // GuestTestimonials
  "testimonials.avgRating": "{avg}/5 average rating across {count} reviews",
  "testimonials.verified": "Verified · Guestly Experiences",
  "testimonials.qualityChecked": "Quality-checked hosts",
  "testimonials.cancellationAlways": "Cancellation, always",
  "testimonials.localGuides": "Guides who know this place",
  // ExperienceGrid
  "grid.loadError": "Something went wrong loading experiences. Please try again later.",
  // AeoSection
  "aeo.exploreHomes": "Explore Guestly Homes",
  // JourneySteps stop types
  "stop.pickup": "Pickup",
  "stop.viewpoint": "Viewpoint",
  "stop.activity": "Activity",
  "stop.campsite": "Campsite",
  "stop.dropoff": "Drop-off",
};

export function useUiTranslations() {
  const { language } = useLanguage();

  const { data: translations } = useQuery({
    queryKey: ["site-translations", language],
    queryFn: async () => {
      if (language === "en") return {};

      // Fetch all rows – Supabase default limit is 1000, so we paginate
      const map: Record<string, string> = {};
      const PAGE_SIZE = 1000;
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const { data } = await supabase
          .from("site_translations")
          .select("string_key, value")
          .eq("language_code", language)
          .range(from, from + PAGE_SIZE - 1);

        if (!data || data.length === 0) {
          hasMore = false;
        } else {
          for (const row of data) {
            map[row.string_key] = row.value;
          }
          from += PAGE_SIZE;
          if (data.length < PAGE_SIZE) hasMore = false;
        }
      }

      return map;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  const t = (key: string): string => {
    if (language === "en") return EN_DEFAULTS[key] ?? key;
    return translations?.[key] ?? EN_DEFAULTS[key] ?? key;
  };

  return { t, language };
}
